// Shared UI primitives styled to match the WellKept design (Tailwind 4 tokens).

import { type ReactNode, type ButtonHTMLAttributes } from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// --- Page container ---------------------------------------------------------

export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('min-h-screen pb-24', className)}>{children}</div>;
}

export function Main({ children, className }: { children: ReactNode; className?: string }) {
  return <main className={cn('max-w-lg mx-auto px-4 py-4', className)}>{children}</main>;
}

// --- Card -------------------------------------------------------------------

export function Card({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-2xl bg-card border border-border',
        onClick && 'cursor-pointer hover:border-primary/30 transition-colors',
        className,
      )}
    >
      {children}
    </div>
  );
}

// --- Button -----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'outline';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-muted',
  ghost: 'text-foreground hover:bg-secondary',
  outline: 'border border-border text-foreground hover:bg-secondary',
  destructive: 'bg-destructive text-white hover:opacity-90',
};

export function Button({ variant = 'primary', fullWidth, className, children, ...rest }: ButtonProps) {
  return (
    <button
      {...rest}
      className={cn(
        'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
    >
      {children}
    </button>
  );
}

// --- Badge ------------------------------------------------------------------

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-secondary text-secondary-foreground',
    accent: 'bg-accent/20 text-accent-foreground',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-destructive/10 text-destructive',
  };
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', tones[tone], className)}>
      {children}
    </span>
  );
}

// --- Spinner / loading / error ---------------------------------------------

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('w-5 h-5 animate-spin text-muted-foreground', className)} />;
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <Spinner className="w-8 h-8" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="mx-4 my-6 rounded-xl bg-destructive/10 text-destructive px-4 py-3 text-sm">{message}</div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
      {icon && <div className="p-4 rounded-2xl bg-secondary mb-4 text-muted-foreground">{icon}</div>}
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {description && <p className="text-sm text-muted-foreground mt-1 max-w-xs">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// --- Modal ------------------------------------------------------------------

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
}: {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-background rounded-3xl border border-border max-h-[85vh] flex flex-col shadow-xl">
        {title && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="text-base font-bold text-foreground">{title}</h2>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-secondary">
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-border">{footer}</div>}
      </div>
    </div>
  );
}

// --- Form inputs ------------------------------------------------------------

export const inputClass =
  'w-full px-3.5 py-2.5 rounded-xl bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

// --- Photo thumbnail (handles missing image) --------------------------------

export function Thumb({
  url,
  alt,
  className,
}: {
  url: string | null | undefined;
  alt: string;
  className?: string;
}) {
  return (
    <div className={cn('relative bg-secondary overflow-hidden flex items-center justify-center', className)}>
      {url ? (
        <img src={url} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ) : (
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">No photo</span>
      )}
    </div>
  );
}

// --- Fit thumbnail ----------------------------------------------------------
// A real fit/wear photo when one exists; otherwise a collage of up to four
// piece photos; otherwise the plain "No photo" placeholder.

export function FitThumb({
  url,
  piecePhotoUrls,
  alt,
  className,
}: {
  url: string | null | undefined;
  piecePhotoUrls?: string[] | null;
  alt: string;
  className?: string;
}) {
  const pieces = (piecePhotoUrls ?? []).slice(0, 4);
  if (url || pieces.length === 0) {
    return <Thumb url={url} alt={alt} className={className} />;
  }
  const cols = pieces.length === 1 ? 'grid-cols-1' : 'grid-cols-2';
  const rows = pieces.length <= 2 ? 'grid-rows-1' : 'grid-rows-2';
  return (
    <div className={cn('relative bg-secondary overflow-hidden grid gap-px', cols, rows, className)}>
      {pieces.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={i === 0 ? alt : ''}
          className="w-full h-full object-cover min-h-0 min-w-0"
          loading="lazy"
        />
      ))}
    </div>
  );
}
